import React from "react";
import {Route, Switch} from "react-router-dom";

// COMPONENTS
import Header from "@components/Header/Header";
import Modal from "@components/Modal/Modal";

// HELPERS
import {connectRedux} from "@helpers/utils";
import api from "./helpers/api";

// REDUCERS
import {actions as appActions} from "@reducers/app";

// ROUTES
import CobRoute from "@routes/Cob/Cob";
import StatusRoute from "@routes/Status/Status";

import Keycloak from "keycloak-js";

export default connectRedux(
    state => ({
        router: state.router,
        app: state.app
    }),
    {
        setAppState: appActions.setState,
        closeErrorModal: appActions.closeErrorModal
    },

    class App extends React.PureComponent
    {
        async componentDidMount()
        {
            this._isMounted = true;
            const {props} = this;
            const {history, location} = props;

            /**
             * Make sure partner site can only be accessed through the correct domain.
             * Meaning: if we are on cob.odinfonder.se and try to access the /auagfonder
             * partner site, we should get an error that the partner does not exist.
             *
             * Also, make sure that when entering the raw domain (cob.odinfonder.se) we
             * redirect to the partner subdirectory automatically (cob.odinfonder.se/odin).
             */
            const partnerDomainMapping = await api("/partner/domains");
            const validPartnerDomains = Object.keys(partnerDomainMapping);
            const hostname = window.location.host.toLowerCase();
            let mappedPartnerSite = null;
            validPartnerDomains.forEach(function(partner)
            {
                if (mappedPartnerSite == null && hostname.startsWith(partner))
                {
                    mappedPartnerSite = partnerDomainMapping[partner];
                }
            });

            /**
             * Partner site has not been set up in app-config.json file
             */
            if (mappedPartnerSite == null)
            {
                console.log(
                    "Partner site '" +
                    hostname +
                    "' not configured correctly. Please check app-config."
                );
                history.replace("/error", {error: {message: "PARTNER_UNDEFINED"}});
            }
            else if (location.pathname === "/")
            {
                // Raw domain, redirect to partner sub-directory
                window.location = "/" + mappedPartnerSite;
            }
            else if (!location.pathname.startsWith("/" + mappedPartnerSite))
            {
                /**
                 * We are trying to access a partner site on an incorrect domain, for example trying to
                 * open the AuAg Fonder site on cob.odinfonder.se (https://cob.odinfonder.se/auagfonder).
                 * This is not allowed.
                 */
                console.log(
                    "Partner site '" +
                    mappedPartnerSite +
                    "' accessed on incorrect domain."
                );
                history.replace("/error", {error: {message: "PARTNER_UNDEFINED"}});
            }
        }

        componentWillUnmount()
        {
            this._isMounted = false;
        }

        componentDidUpdate(prevProps)
        {
            const {props} = this;
            const {app, actions, location} = props;

            // Check history updates (using location prop)
            if (prevProps.location !== location)
            {
                actions.setAppState({prevLocation: prevProps.location});
            }

            if (prevProps.app.partner !== app.partner && app.partner)
            {
                // Setup document
                app.partner.setupDocument();

                if (app.partner.faSSO)
                {
                    // init faSSO
                    const getSsoRedirectUri = () =>
                    {
                        const url = new URL(
                            `${process.env.PUBLIC_URL}/keycloak-silent-check-sso.html`,
                            window.location.origin
                        );
                        return url.href;
                    };

                    const keycloakInitConfig = {
                        onLoad: "check-sso",
                        silentCheckSsoRedirectUri: getSsoRedirectUri(),
                        pkceMethod: "S256"
                    };

                    const keycloak = new Keycloak(`${process.env.PUBLIC_URL}/keycloak.json`);

                    keycloak.init(keycloakInitConfig).then(async function(authenticated)
                    {
                        console.log("authenticated", authenticated);
                        // Only works once straight after login, then it always fails with the following error:
                        // Refused to frame 'https://platform3.fasolutions.com/' because an ancestor violates the following Content Security Policy directive: "frame-ancestors 'self'".

                        // Chat GPT solution:
                        // To resolve this issue, the owner of the website at 'https://platform3.fasolutions.com/' would need to adjust their CSP policy to include the domain or origin of the website where you encountered the error. Alternatively, they may need to change the policy directive to allow embedding within iframes from any source by using a wildcard ('*') or specific origins. However, these changes should only be made if it aligns with their security requirements and does not introduce any vulnerabilities.
                        if (!authenticated)
                        {
                            return keycloak.login();
                        }

                        const {token} = keycloak;
                        const profile = await keycloak.loadUserProfile();

                        props.actions.setAppState({faAuth: {profile, token}});
                    }).catch(function(err)
                    {
                        console.log(err);
                    });
                }
            }
        }

        render()
        {
            const {props} = this;
            const {app} = props;
            const {partner, session, errorModal} = app;

            let backgroundContainer = null;
            if (partner)
            {
                if (partner.backgroundUrl.startsWith("#"))
                {
                    backgroundContainer = (
                        <div
                            id="app-bg"
                            className="App-bg"
                            style={{background: `${partner.backgroundUrl}`}}
                        ></div>
                    );
                }
                else
                {
                    backgroundContainer = (
                        <div
                            id="app-bg"
                            className="App-bg"
                            style={{backgroundImage: `url(${partner.backgroundUrl})`}}
                        ></div>
                    );
                }
            }

            let appContentClassNames = "App-content";
            if (partner && partner.iframed)
            {
                appContentClassNames = appContentClassNames + " iframed";
            }

            return (
                <div className="App">
                    {<CustomStyleSheet {...props} />}

                    {backgroundContainer}

                    <div className={appContentClassNames} data-iframe-height>
                        <Header partner={partner} session={session} />

                        <Switch>
                            <Route path="/error" component={StatusRoute} exact />

                            <Route path="/:partnerName/error" component={StatusRoute} exact />
                            <Route
                                path="/:partnerName/success"
                                component={StatusRoute}
                                exact
                            />
                            <Route
                                path="/:partnerName/pending"
                                component={StatusRoute}
                                exact
                            />
                            <Route path="/:partnerName" component={CobRoute} exact />

                            <Route component={CobRoute} />
                        </Switch>
                    </div>

                    {(errorModal.title || errorModal.body) && (
                        <Modal
                            visible={errorModal.visible}
                            title={errorModal.title}
                            status="error"
                        >
                            {typeof errorModal.body === "string" ? (
                                <p>{errorModal.body}</p>
                            ) : (
                                errorModal.body
                            )}

                            <Modal.ButtonsContainer>
                                <Modal.Button
                                    label={errorModal.closeButtonLabel || "Stäng"}
                                    appearance="primary"
                                    onClick={
                                        errorModal.onCloseButtonClick || this._closeErrorModal
                                    }
                                />
                            </Modal.ButtonsContainer>
                        </Modal>
                    )}
                </div>
            );
        }

        // Internal methods
        _closeErrorModal = async () =>
        {
            const {actions} = this.props;
            await actions.closeErrorModal();
        };
    }
);

// PRIVATE COMPONENTS
function CustomStyleSheet(props)
{
    const {partner} = props.app;
    if (!partner)
    {
        return null;
    }

    // Stringify styles
    const styles = _getCustomStyles(props);
    const result = styles
        .map((style, i) =>
        {
            const styleName = Object.keys(style)[0];
            const values = style[styleName];
            const keys = Object.keys(values);
            const returnValue = [];
            for (let j = 0, n = keys.length; j < n; j++)
            {
                const key = keys[j];
                const value = values[key];
                if (value)
                {
                    returnValue.push(
                        `${key}:${value}` + (j !== keys.length - 1 ? ";" : "")
                    );
                }
            }
            return `${styleName}{${returnValue.join("")}}`;
        })
        .join(" ");

    return <style type="text/css">{result}</style>;
}

function _getCustomStyles({app})
{
    const {partner} = app;
    const {accentColors, borderStyle, containerShadow} = partner;
    const parseValue = (value, suffix) =>
    {
        if (typeof value === "undefined" || value === null || value === "")
        {
            return null;
        }
        if (value === 0 || value === "0")
        {
            return "0";
        }
        return (
            value +
            (typeof value === "number" ? "px" : "") +
            (suffix ? " " + suffix : "")
        );
    };
    const defaultValues = {
        fieldBorderRadius:
            borderStyle.radius === "none"
                ? "0"
                : borderStyle.radius === "round"
                    ? "10px"
                    : null
    };

    // This is an array containing one-key objects
    return [
        // General
        {
            h2: {
                color: parseValue(accentColors.primary)
            }
        },

        {
            h3: {
                color: parseValue(accentColors.secondary)
            }
        },

        // App
        {
            ".App-content": {
                "border-radius":
                    borderStyle.radius === "round"
                        ? "15px"
                        : defaultValues.fieldBorderRadius,
                "box-shadow": !containerShadow ? "none" : null
            }
        },

        // Button
        {
            ".Button": {
                "border-width": parseValue(borderStyle.width)
            }
        },
        {
            ".Button.primary": {
                color: parseValue(accentColors.primary)
            }
        },
        {
            ".Button.primary.filled": {
                "background-color": parseValue(accentColors.primary)
            }
        },
        {
            ".Button.primary:not(.filled)": {
                "border-color": parseValue(accentColors.primary)
            }
        },

        {
            ".Button.secondary": {
                color: parseValue(accentColors.secondary)
            }
        },
        {
            ".Button.secondary.filled": {
                "background-color": parseValue(accentColors.secondary)
            }
        },
        {
            ".Button.secondary:not(.filled)": {
                "border-color": parseValue(accentColors.secondary)
            }
        },

        // Checkbox
        {
            ".Checkbox-checkbox": {
                "border-width": parseValue(borderStyle.width),
                "border-radius":
                    borderStyle.radius === "round"
                        ? "5px"
                        : defaultValues.fieldBorderRadius
            }
        },

        // Cob
        {
            ".Cob-buttons:before": {
                height: parseValue(borderStyle.width)
            }
        },

        // Cob-SectionNumber
        {
            ".Cob-SectionNumber:before": {
                width: parseValue(borderStyle.width)
            }
        },
        {
            ".Cob-SectionNumber-circle": {
                "border-radius":
                    borderStyle.radius === "round"
                        ? "100%"
                        : defaultValues.fieldBorderRadius,
                "border-width": parseValue(borderStyle.width)
            }
        },

        // Select
        {
            ".Select-inner": {
                "border-width": parseValue(borderStyle.width)
            }
        },
        {
            ".Select.focus:not(.valid) .Select-inner": {
                "border-color": parseValue(accentColors.secondary)
            }
        },
        {
            ".Select-dropdown-container": {
                width: borderStyle.width
                    ? `calc( 100% + ${parseValue(borderStyle.width * 2)} )`
                    : null
            }
        },
        {
            ".Select-dropdown-container:before": {
                left: parseValue(borderStyle.width),
                right: parseValue(borderStyle.width)
            }
        },
        {
            ".Select-dropdown": {
                "border-width": parseValue(borderStyle.width),
                "border-color": parseValue(accentColors.secondary)
            }
        },
        {
            ".Select.focus .Select-dropdown": {
                "border-bottom-left-radius": defaultValues.fieldBorderRadius,
                "border-bottom-right-radius": defaultValues.fieldBorderRadius
            }
        },
        {
            ".Select-dropdown-option.highlight, .Select-dropdown-option.selected": {
                color: parseValue(accentColors.secondary)
            }
        },
        {
            ".Select.focus .Select-arrow": {
                color: parseValue(accentColors.secondary)
            }
        },

        // TextField
        {
            ".TextField": {
                "border-width": parseValue(borderStyle.width)
            }
        },
        {
            ".TextField:not(.valid).focus": {
                "border-color": parseValue(accentColors.secondary)
            }
        },
        {
            ".TextField.focus:not(.invalid) .TextField-status .required-symbol": {
                color: parseValue(accentColors.secondary)
            }
        },

        // Multiple selectors
        {
            ".Button": {
                "border-radius":
                    borderStyle.radius === "round"
                        ? "24px"
                        : defaultValues.fieldBorderRadius
            }
        },
        {
            ".Select, .TextField": {
                "border-radius": defaultValues.fieldBorderRadius
            }
        }
    ];
}



// WEBPACK FOOTER //
// ./src/App.js